import type { ReactNode } from "react";
import type { Class } from "@common/types/class";
import { AvatarTooltip } from "./AvatarTooltip";
import { ClassAvatar } from "./ClassAvatar";
import { LabelledAvatarGroup } from "./LabelledAvatarGroup";

export type ClassAvatarGroupClass = Pick<
    Class,
    "id" | "label" | "iconColor" | "order"
>;

export interface ClassAvatarGroupProps {
    /** Shown in `order`, whatever order they come in. */
    classes: readonly ClassAvatarGroupClass[];
    /** Visible avatars before the +N chip (see `AvatarGroup`). @default 4 */
    max?: number;
    /** Trailing text after the stack, e.g. "3 classes". */
    label?: ReactNode;
    className?: string;
}

/**
 * A 20px stack of class diamonds in display order, each with its label in a
 * tooltip and a right-click copy of its ID. 20px, not the 16px of entity
 * stacks: a diamond fills half its box, so this matches a 16px square in weight
 * and leaves its letter room.
 */
export const ClassAvatarGroup = ({
    classes,
    max,
    label,
    className,
}: Readonly<ClassAvatarGroupProps>) => (
    <LabelledAvatarGroup
        items={[...classes].sort((a, b) => a.order - b.order)}
        getKey={(classItem) => classItem.id}
        getLabel={(classItem) => classItem.label}
        renderAvatar={(classItem) => (
            <AvatarTooltip content={classItem.label}>
                <ClassAvatar class={classItem} copyId={classItem.id} />
            </AvatarTooltip>
        )}
        size="sm"
        max={max}
        label={label}
        className={className}
    />
);
